import React from 'react';
import { Calendar, TrendingUp, TrendingDown, Star, Megaphone } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { Gauge } from '../game/Gauge';
import GlossaryTerm from '../GlossaryTerm';

export function KpiStrip({ day, month, year, working, phase, treasury, dailyDelta, reputation, notoriety }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-strip">
      <div className="surface-card p-3.5">
        <div className="flex items-center gap-1.5 overline">
          <Calendar className="w-3 h-3" /> {t('date_label')}
        </div>
        <p className="data-stat text-foreground text-base mt-1.5 leading-tight">
          J{day} · M{month} · {year}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {working ? t('working_day') : t('weekend')}
          {phase > 1 && <span className="ml-1 text-accent font-mono">P{phase}</span>}
        </p>
      </div>

      <div className="surface-card p-3.5">
        <p className="overline">
          <GlossaryTerm conceptId="tresorerie">{t('treasury')}</GlossaryTerm>
        </p>
        <p
          className={`data-stat text-xl md:text-2xl font-bold mt-1.5 ${treasury >= 0 ? 'text-foreground' : 'text-danger'}`}
          data-testid="treasury-value"
        >
          {formatMoney(treasury)}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5">{t('current_balance')}</p>
      </div>

      <div
        className="surface-card p-3.5 relative group cursor-help"
        title={t('kpi_daily_delta_tooltip')}
        data-testid="daily-delta-tile"
      >
        <p className="overline">{t('daily_delta')}</p>
        <div className="flex items-center gap-1 mt-1.5">
          {dailyDelta >= 0
            ? <TrendingUp className="w-4 h-4 text-money" />
            : <TrendingDown className="w-4 h-4 text-danger" />}
          <p className={`data-stat text-lg font-bold ${dailyDelta >= 0 ? 'text-money' : 'text-danger'}`}>
            {formatMoney(dailyDelta)}
          </p>
        </div>
        <p className="text-[10px] text-slate-400 mt-0.5">{t('net_last_day')} <span className="text-slate-500">· {t('kpi_smoothed_suffix')}</span></p>
      </div>

      <div className="surface-card p-3.5 relative group cursor-help" data-testid="reputation-tile">
        <p className="overline">{t('reputation')}</p>
        <div className="flex items-center gap-1.5 mt-1.5">
          <Star className="w-4 h-4 text-warn" />
          <span className="data-stat text-lg font-bold text-foreground" data-testid="reputation-value">{Math.round(reputation)}</span>
          <span className="text-[10px] text-slate-500">/100</span>
        </div>
        <div className="mt-1.5">
          <Gauge value={reputation} type="satisfaction" />
        </div>
        <div
          className="pointer-events-none absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1 w-64 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid="reputation-tooltip"
        >
          <div className="bg-ink-900 border border-warn/40 rounded-md p-3 shadow-xl text-[11px] text-slate-300 leading-snug">
            <p className="font-bold text-warn mb-1 uppercase tracking-wider text-[10px]">{t('kpi_how_it_evolves')}</p>
            <ul className="space-y-0.5">
              <li>✅ {t('kpi_rep_evo_1')}</li>
              <li>✅ {t('kpi_rep_evo_2')}</li>
              <li>✅ {t('kpi_rep_evo_3')}</li>
              <li>❌ {t('kpi_rep_evo_4')}</li>
              <li>❌ {t('kpi_rep_evo_5')}</li>
            </ul>
            <p className="mt-2 text-slate-500 text-[10px] italic">{t('kpi_rep_influence')}</p>
          </div>
        </div>
      </div>

      <div className="surface-card p-3.5 relative group cursor-help" data-testid="notoriety-tile">
        <p className="overline">
          <GlossaryTerm conceptId="notoriete">{t('notoriety_short')}</GlossaryTerm>
        </p>
        <div className="flex items-center gap-1.5 mt-1.5">
          <Megaphone className="w-4 h-4 text-accent" />
          <span className="data-stat text-lg font-bold text-foreground" data-testid="notoriety-value">{Math.round(notoriety || 0)}</span>
          <span className="text-[10px] text-slate-500">/100</span>
        </div>
        <div className="mt-1.5">
          <Gauge value={notoriety || 0} type="satisfaction" />
        </div>
        <div
          className="pointer-events-none absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1 w-64 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid="notoriety-tooltip"
        >
          <div className="bg-ink-900 border border-accent/40 rounded-md p-3 shadow-xl text-[11px] text-slate-300 leading-snug">
            <p className="font-bold text-accent mb-1 uppercase tracking-wider text-[10px]">{t('kpi_how_it_evolves')}</p>
            <ul className="space-y-0.5">
              <li>✅ {t('kpi_noto_evo_1')}</li>
              <li>✅ {t('kpi_noto_evo_2')}</li>
              <li>✅ {t('kpi_noto_evo_3')}</li>
              <li>✅ {t('kpi_noto_evo_4')}</li>
              <li>⌛ {t('kpi_noto_evo_5')}</li>
            </ul>
            <p className="mt-2 text-slate-500 text-[10px] italic">{t('kpi_noto_influence')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
